import { memo, useCallback, type ReactNode } from "react";
import { Pressable, type GestureResponderEvent } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View } from "../View";
import { DefaultItemContent, getAccessibilityLabel, isParentItem, styles } from "./ItemContent";
import type {
  NavSidePanelDisclosureIconRenderProps,
  NavSidePanelItem,
  NavSidePanelItemContentRenderProps,
  NavSidePanelItemRenderProps,
} from "./NavSidePanel.types";

interface NavSidePanelItemNodeProps {
  readonly item: NavSidePanelItem;
  readonly depth: number;
  readonly depthIndent: number;
  readonly expandedItemIds: readonly string[];
  readonly minimized: boolean;
  readonly onToggleExpanded: (itemId: string) => void;
  readonly onItemPress?: (item: NavSidePanelItem, event: GestureResponderEvent) => void;
  readonly renderItem?: (props: NavSidePanelItemRenderProps) => ReactNode;
  readonly renderItemContent?: (props: NavSidePanelItemContentRenderProps) => ReactNode;
  readonly renderDisclosureIcon?: (props: NavSidePanelDisclosureIconRenderProps) => ReactNode;
}

export const NavSidePanelItemNode = memo(function NavSidePanelItemNode({
  item,
  depth,
  depthIndent,
  expandedItemIds,
  minimized,
  onToggleExpanded,
  onItemPress,
  renderItem,
  renderItemContent,
  renderDisclosureIcon,
}: NavSidePanelItemNodeProps) {
  const { theme } = useUnistyles();
  const hasChildren = isParentItem(item);
  const expanded = !minimized && hasChildren && expandedItemIds.includes(item.id);
  const selected = item.selected === true;
  const disabled = item.disabled === true;

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      if (disabled) return;
      if (item.onPress) {
        item.onPress(event);
      } else if (hasChildren && !minimized) {
        onToggleExpanded(item.id);
      }
      onItemPress?.(item, event);
    },
    [disabled, hasChildren, item, minimized, onItemPress, onToggleExpanded],
  );

  const handleChevronPress = useCallback(() => {
    if (!disabled) {
      onToggleExpanded(item.id);
    }
  }, [disabled, item.id, onToggleExpanded]);

  const contentProps: NavSidePanelItemContentRenderProps = {
    item,
    depth,
    expanded,
    hasChildren,
    selected,
    disabled,
    minimized,
  };

  const hasIndependentChevron = hasChildren && item.onPress !== undefined && !minimized;

  const row = (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      accessibilityRole={hasIndependentChevron ? "none" : "button"}
      accessibilityLabel={getAccessibilityLabel(item)}
      accessibilityState={{ disabled, expanded: hasChildren ? expanded : undefined, selected }}
      testID={item.testID}
      style={({ pressed }) => [
        styles.item,
        minimized ? styles.itemMinimized : undefined,
        {
          paddingLeft: minimized ? theme.spacing.lg : theme.spacing.xl + depth * depthIndent,
          backgroundColor: selected ? theme.colors.surface2.selected : "transparent",
          opacity: pressed && !disabled ? 0.72 : 1,
        },
      ]}
    >
      {renderItemContent ?
        renderItemContent(contentProps)
      : <DefaultItemContent
          {...contentProps}
          {...(renderDisclosureIcon ? { renderDisclosureIcon } : {})}
          onChevronPress={hasIndependentChevron ? handleChevronPress : undefined}
        />
      }
    </Pressable>
  );

  return (
    <View>
      {renderItem ?
        renderItem({
          ...contentProps,
          onPress: handlePress,
          defaultElement: row,
        })
      : row}
      {hasChildren && expanded && (
        <View style={styles.children}>
          {isParentItem(item) && item.renderChildren ?
            item.renderChildren({ minimized })
          : item.children.map((child) => (
              <NavSidePanelItemNode
                key={child.id}
                item={child}
                depth={depth + 1}
                depthIndent={depthIndent}
                expandedItemIds={expandedItemIds}
                minimized={minimized}
                onToggleExpanded={onToggleExpanded}
                {...(onItemPress ? { onItemPress } : {})}
                {...(renderItem ? { renderItem } : {})}
                {...(renderItemContent ? { renderItemContent } : {})}
                {...(renderDisclosureIcon ? { renderDisclosureIcon } : {})}
              />
            ))
          }
        </View>
      )}
    </View>
  );
});
